import Text from '@hixme-ui/text'
import { selectors as authSelectors } from '@hixme/auth-ui'
import {
  ROLE_BROKER,
  ROLE_CLIENT_ADMIN,
  ROLE_PLATFORM_HIXME_ADMIN,
} from '@hixme/role-policy'
import { Button } from '@sureco/design-system'
import { Layout } from '@surecompanies/core_components'
import { message, Space, Switch } from 'antd'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import {
  BULK_TERMINATIONS_NAV_HREF,
  navAbilitiesForHref,
} from 'enrollment-platform/rbac/nav-ability-map'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import Page from 'layouts/Page'
import { ENVIROMENT_DEV, getEnvironment } from 'modules/app-config/environment'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { browserHistory, withRouter } from 'react-router'
import { selectors as companySelectors } from 'store/modules/companies'
import BreadcrumbNav from '../../../components/Breadcrumbs'
import PageWrapper from '../../../PageWrapper'
import '../CensusReview/pages/CensusReview.scss'
import ActivityHistory from '../components/ActivityHistoryGrid'
import CensusDataFilter from '../components/CensusDataFilter'
import CensusManagementUploadModal from '../components/CensusManagementUploadModal'
import CompletedGrid from '../components/CompletedGrid'
import CensusTemplateBanner from '../components/DownloadCensusTemplate/CensusTemplateBanner'
import GenerateTestCensusModal from '../components/GenerateTestCensusModal'
import NewAndUpdatedRecords from '../components/NewAndUpdatedRecordsGrid'
import { useCensusV2Clients } from '../hooks/use-census-v2-clients'
import { useHrisSyncCensus } from '../hooks/useHrisSyncCensus'
import { CensusClientFilter } from '../types/census-client-filter'
import { isQueuedUpload } from '../utils/census-upload-job'

const { Content } = Layout

function CensusManage() {
  const [isModalVisible, setIsModalVisible] = useState(false) // State to control modal visibility
  const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false)
  const [fileUpdated, setFileUpdated] = useState<boolean>(false)
  const [filter, setFilter] = useState<CensusClientFilter>({
    includeTestCompanies: getEnvironment() === ENVIROMENT_DEV,
  })
  const { clients, error, isLoading } = useCensusV2Clients()

  // Get companies from Redux store to get isTestClient info
  const companiesFromStore = useSelector(
    companySelectors.companyCollectionSelector
  )

  // Merge isTestClient from Redux store into clients
  const clientsWithTestInfo = useMemo(() => {
    const companyMap = companiesFromStore.reduce(
      (acc: Record<string, boolean>, company: any) => {
        acc[company.id] = company.isTestClient ?? false
        return acc
      },
      {}
    )
    return clients.map((client) => ({
      ...client,
      isTestClient: companyMap[client.id] ?? false,
    }))
  }, [clients, companiesFromStore])

  // Check if current user is Hixme Admin
  const userRole = useSelector(authSelectors.getRole)
  const isPlatformAdmin = userRole === ROLE_PLATFORM_HIXME_ADMIN
  // HR and brokers see their own clients' unfinished uploads; the API scopes the rows
  const canDismissUploads = isPlatformAdmin || userRole === ROLE_CLIENT_ADMIN
  const canSeeUploadQueue = canDismissUploads || userRole === ROLE_BROKER
  const isHixmeAdmin = [ROLE_PLATFORM_HIXME_ADMIN, ROLE_BROKER].includes(
    userRole
  )
  // Dev-only census file generator: platform admins on the dev environment only.
  const showDevTools = isPlatformAdmin && getEnvironment() === ENVIROMENT_DEV
  const { showSyncCensusButton, isSyncingCensus, showSyncPromptModal } =
    useHrisSyncCensus({
      clientPublicKey: filter?.selectedClient?.id,
      clientName: filter?.selectedClient?.name,
      isPlatformAdmin,
    })
  const bulkTerminationAbility = navAbilitiesForHref(
    BULK_TERMINATIONS_NAV_HREF
  )?.[0]
  const { allowed: hasBulkTerminationAbility } = usePermission(
    bulkTerminationAbility?.action ?? RbacActions.Manage,
    bulkTerminationAbility?.subject ?? RbacSubjects.UsRestrictedData
  )
  const showBulkTerminations =
    !bulkTerminationAbility || hasBulkTerminationAbility

  if (error) {
    const msg = `Error loading companies: ${error.message}`
    console.error(msg)
    message.error(msg, 5)
  }

  const clientNameLookup = useMemo(() => {
    return clients.reduce((acc, client) => {
      acc[client.id] = client.name
      return acc
    }, {})
  }, [clients]) // Rebuild lookup only if clients change

  const handleUploadSuccess = async (_uploaded?: boolean, result?: unknown) => {
    try {
      setIsModalVisible(false)
      if (isQueuedUpload(result)) {
        message.success(
          canSeeUploadQueue
            ? 'Upload queued. Follow its progress at the top of Drafts.'
            : 'Upload received. It will appear in Drafts once it is processed.'
        )
      } else {
        message.success('File uploaded successfully!')
      }
      // this is just a toggle to refresh the child
      setFileUpdated(!fileUpdated)
    } catch (error) {
      const msg = (error as any)?.response?.data?.message ?? error
      message.error(`Failed to upload file: ${msg}`)
    }
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleToggle = (checked: boolean) => {
    setFilter({ ...filter, includeTestCompanies: checked })
  }

  // If the current HR Admin can't view any clients for which V2 is enabled, they are unauthorized to see V2 pages
  if (!error && !isLoading && !clients.length) {
    return <h2>Access Denied</h2>
  }

  return (
    <PageWrapper>
      <Page
        title="Census Management"
        action={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Test Company Toggle */}
            {isHixmeAdmin && (
              <Space align="center">
                <Switch
                  checked={filter?.includeTestCompanies ?? true}
                  onChange={handleToggle}
                  size="default"
                />
                <Text style={{ fontSize: '16px', marginRight: '10px' }}>
                  Include test companies
                </Text>
              </Space>
            )}
            {showSyncCensusButton && (
              <Button
                onClick={showSyncPromptModal}
                loading={isSyncingCensus}
                disabled={isSyncingCensus}
              >
                HRIS Sync
              </Button>
            )}
            <Button onClick={() => setIsModalVisible(true)}>
              Add and Update
            </Button>
            {showBulkTerminations && (
              <Button
                onClick={() =>
                  browserHistory.push(`v2-census-management/bulk-terminations`)
                }
              >
                Bulk Terminations
              </Button>
            )}
            {showDevTools && (
              <Button onClick={() => setIsGenerateModalVisible(true)}>
                Generate Test File (Dev)
              </Button>
            )}
          </div>
        }
        breadcrumb={
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <BreadcrumbNav items={[{ title: 'Census Management' }]} />
            <CensusTemplateBanner />
          </div>
        }
      >
        {(isHixmeAdmin || clientsWithTestInfo?.length > 1) && (
          <CensusDataFilter
            filter={filter}
            onFilterChange={setFilter}
            clients={clientsWithTestInfo}
          />
        )}
        <CensusManagementUploadModal
          open={isModalVisible}
          onOk={handleUploadSuccess}
          onCancel={handleCancel}
        />
        {showDevTools && (
          <GenerateTestCensusModal
            open={isGenerateModalVisible}
            onClose={() => setIsGenerateModalVisible(false)}
            onGenerated={() => setFileUpdated((prev) => !prev)}
          />
        )}
        <Layout style={{ backgroundColor: '#f6f9fc' }}>
          <Content style={{}}>
            <NewAndUpdatedRecords
              fileUpdated={fileUpdated}
              clientNameLookup={clientNameLookup}
              filter={filter}
              showQueueRows={canSeeUploadQueue}
              canDismissQueueRows={canDismissUploads}
            />
            <ActivityHistory
              clientNameLookup={clientNameLookup}
              filter={filter}
            />
            <CompletedGrid
              clientNameLookup={clientNameLookup}
              filter={filter}
            />
          </Content>
        </Layout>
      </Page>
    </PageWrapper>
  )
}

export default withRouter(CensusManage)
